// DetailHeader — the identity band shared by the event and brand detail pages,
// so both read alike: back link on top (with the page type on the right, in
// the type's colour), then avatar + name with the macro category icons
// beside it, and the info pills below.
import React from 'react';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import CategoryList from 'components/CategoryList';

const TYPE_TONES = {
  event: 'text-brand-deep',
  brand: 'text-ink-strong',
};

export function DetailPill({ icon: Icon, children, title }) {
  return (
    <span
      title={title}
      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white ring-1 ring-line text-[12.5px] font-medium text-ink-strong max-w-full"
    >
      {Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}
      <span className="truncate">{children}</span>
    </span>
  );
}

const DetailHeader = ({
  backLabel,
  onBack,
  typeLabel,
  tone = 'event',
  avatar,
  title,
  pills,
  categories,
  aside,
}) => (
  <div className="bg-gray-100 border-b border-gray-300">
    <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-7 pt-6 pb-8">
      <div className="flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-[13px] text-gray-600 hover:text-ink-strong"
        >
          <ChevronLeftIcon className="w-3.5 h-3.5" />
          {backLabel}
        </button>
        {typeLabel && (
          <span
            className={`text-[13px] font-extrabold uppercase tracking-[0.2em] ${TYPE_TONES[tone]}`}
          >
            {typeLabel}
          </span>
        )}
      </div>

      {/* Grid so the pills can sit full-width under the avatar on mobile and
          tuck under the name from sm up. */}
      <div className="mt-4 grid grid-cols-[auto_minmax(0,1fr)] lg:grid-cols-[auto_minmax(0,1fr)_auto] gap-x-4 sm:gap-x-7 items-start">
        <div className="col-start-1 row-start-1 sm:row-span-2">{avatar}</div>

        <div className="col-start-2 row-start-1 min-w-0 pt-1">
          <h1
            title={title}
            className="text-[24px] sm:text-[36px] font-extrabold tracking-tight text-ink-strong leading-tight break-words [overflow-wrap:anywhere] sm:truncate"
          >
            {title}
          </h1>

          {Array.isArray(categories) && categories.length > 0 && (
            <div className="mt-3">
              <CategoryList categories={categories} onlyMacro withLabel />
            </div>
          )}
        </div>

        {pills && (
          <div className="row-start-2 col-span-2 sm:col-span-1 sm:col-start-2 mt-4 sm:mt-3 flex flex-wrap items-center gap-2">
            {pills}
          </div>
        )}

        {aside && (
          <div className="hidden lg:flex col-start-3 row-start-1 row-span-2 self-end items-center gap-2">
            {aside}
          </div>
        )}
      </div>
    </div>
  </div>
);

export default DetailHeader;
